import { Injectable, InjectionToken, inject } from '@angular/core';
import { getRegisteredDataSources, DataSourceMeta, SummarySection } from './data-source.decorator';
import { AgentRegistryService } from '../services/agent-registry.service';
import { AgentCategory } from '../models/agent-config.model';
import { AuthenticationService } from '@omni/services/authentication.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';

/** Multi-provider token: every @AIDataSource provider service registers here. */
export const AI_DATA_SOURCES = new InjectionToken<readonly object[]>('AI_DATA_SOURCES');

/** Context handed to each source provider. */
export interface DataSourceContext {
  /** Contact/account id. Undefined for 'spotlight' — a territory-wide snapshot with no single record. */
  recordId?: string;
  /** The data-source id that selected this method. A method serving several entities (e.g.
   *  `contact_opportunities` / `account_opportunities`) tells them apart by this. */
  dataSourceId: string;
}

interface SourceEntry { meta: DataSourceMeta; instance: object; }

@Injectable({ providedIn: 'root' })
export class AiDataSourceRegistry {
  #entries: SourceEntry[] | null = null;
  private readonly providers: readonly object[] = inject(AI_DATA_SOURCES, { optional: true }) ?? [];
  readonly #agentRegistry = inject(AgentRegistryService);
  readonly #auth = inject(AuthenticationService);

  /**
   * Deterministically assemble the structured sections a surface grounds on. The agent config's
   * configured ids (`AgentConfig.dataSources`, parsed from the `kind:'device'` sources in
   * `indskr_groundingconfig`) select the providers; each runs in registration (decoration) order,
   * independent of config order. The matched id rides on `ctx.dataSourceId` so a method serving
   * several entities knows which one fired; a `string[]` return is auto-wrapped into a section.
   *
   * The category is passed explicitly — the AI Summary card and Nova Insights use DIFFERENT
   * agent-config rows for the same record (per-surface configurability), so the caller decides
   * which row drives the context. Resolution goes through AgentRegistryService so the role-aware
   * winner (the same row whose instructions the agent call uses) drives it. No config / no device
   * sources ⇒ `null` (nothing to assemble — grounding is purely what the config selects).
   */
  async resolve(category: AgentCategory, ctx: { recordId?: string }): Promise<SummarySection[] | null> {
    const want = new Set(this.#agentRegistry.getAgent(category)?.dataSources ?? []);
    const sections: SummarySection[] = [];
    for (const entry of this.#getEntries()) {
      if (!want.has(entry.meta.dataSourceId)) continue;
      // A source tied to a feature action only grounds when the rep's role has that tool —
      // the summary never carries data the app itself hides.
      if (entry.meta.featureAction && !this.#auth.hasFeatureAction(entry.meta.featureAction as FeatureActionsMap)) {
        console.info(`[AiDataSourceRegistry] source '${entry.meta.dataSourceId}' skipped — feature action '${entry.meta.featureAction}' is disabled for this role`);
        continue;
      }
      try {
        const method = (entry.instance as Record<string, (c: DataSourceContext) => Promise<SummarySection | string[] | null>>)[entry.meta.methodName];
        const section = this.#normalizeSection(await method.apply(entry.instance, [{ ...ctx, dataSourceId: entry.meta.dataSourceId }]), entry.meta);
        if (section) {
          sections.push(section);
        } else {
          // A configured source with no data is omitted from the grounding. Log it so the
          // omission is observable — a configured-but-empty source must stay distinguishable
          // from an unconfigured one.
          console.info(`[AiDataSourceRegistry] source '${entry.meta.dataSourceId}' is configured but resolved to no data — omitted from grounding`);
        }
      } catch (e) {
        console.warn(`[AiDataSourceRegistry] source '${entry.meta.dataSourceId}' failed:`, e);
      }
    }
    return sections.length > 0 ? sections : null;
  }

  /** Coerce a provider result into a SummarySection: string[] → wrapped; null/empty → null. */
  #normalizeSection(result: SummarySection | string[] | null, meta: DataSourceMeta): SummarySection | null {
    if (!result) return null;
    const title = meta.label ?? meta.dataSourceId;
    if (Array.isArray(result)) return result.length > 0 ? { title, data: result } : null;
    return { title: result.title || title, data: result.data };
  }

  /** Flat list of all decorated sources, in registration order. */
  #getEntries(): SourceEntry[] {
    if (this.#entries) return this.#entries;
    const entries: SourceEntry[] = [];
    for (const instance of this.providers) {
      for (const meta of getRegisteredDataSources(instance.constructor)) {
        entries.push({ meta, instance });
      }
    }
    this.#entries = entries;
    return entries;
  }
}
